import { dispatchDialogInitialized } from "./pent-core/index.js"

/**
 * PlDialog - Base custom element for dialog components
 * Handles open/close, keyboard navigation, and focus management for native <dialog> elements
 */
export class PlDialog extends HTMLElement {
  // Private fields
  #dialog = null
  #isInitialized = false
  #closeOnEscape = true
  #closeOnOutsideClick = true
  #isInterceptingClose = false
  #isClosingProgrammatically = false

  constructor() {
    super()
    this.handleKeydown = this.handleKeydown.bind(this)
    this.handleClick = this.handleClick.bind(this)
  }

  connectedCallback() {
    this.#init()
  }

  disconnectedCallback() {
    this.#cleanup()
  }

  #init() {
    // Find dialog element (could be self or child)
    this.#dialog = this.querySelector("dialog") || (this instanceof HTMLDialogElement ? this : null)

    if (!this.#dialog) {
      console.error("PlDialog: No <dialog> element found", this)
      return
    }

    // Get configuration from data attributes
    this.#closeOnEscape = this.dataset.closeOnEscape !== "false"
    this.#closeOnOutsideClick = this.dataset.closeOnOutsideClick !== "false"

    // Add event listeners
    this.#attachListeners()

    // Mark as initialized
    this.#isInitialized = true
    dispatchDialogInitialized(this)
  }

  #attachListeners() {
    this.#dialog.addEventListener("toggle", (event) => {
      const isOpen = event.newState === "open"
      console.log("[pl-dialog] Toggle event - newState:", event.newState, "from:", event.target.id)

      // Mirror state to the wrapper component for CSS styling/transitions
      if (isOpen) {
        // Remove closing class if we're not in the middle of intercepting a close
        if (!this.#isInterceptingClose) {
          this.#dialog.classList.remove("closing")
        }
        this.setAttribute("open", "")
        document.documentElement.classList.add("pl-dialog-open")
        this.#focusFirstElement()
        this.dispatchEvent(new CustomEvent("pl-dialog:opened", { bubbles: true }))

        // If we were intercepting, start the close animation now
        if (this.#isInterceptingClose) {
          this.#isInterceptingClose = false
          // Use next frame to ensure dialog is fully visible before animating
          requestAnimationFrame(() => {
            this.close()
          })
        }
      } else {
        // If dialog is closing but we don't have the closing class or we didn't initiate it,
        // it means it was closed externally (backdrop click)
        if (!this.#dialog.classList.contains("closing") && !this.#isClosingProgrammatically) {
          // Prevent the close by reopening immediately
          this.#isInterceptingClose = true
          if (typeof this.#dialog.showPopover === "function") {
            this.#dialog.showPopover()
          } else if (typeof this.#dialog.showModal === "function") {
            this.#dialog.showModal()
          } else {
            this.#dialog.setAttribute("open", "")
          }
          // The reopen will trigger another toggle event (open), which will handle the close animation
          return
        }

        // Normal cleanup after our animated close completes
        this.#isClosingProgrammatically = false
        this.#dialog.classList.remove("closing")
        this.removeAttribute("open")
        document.documentElement.classList.remove("pl-dialog-open")
        this.dispatchEvent(new CustomEvent("pl-dialog:closed", { bubbles: true }))
      }
    })
  }

  #cleanup() {
    if (this.#dialog) {
      this.#dialog.removeEventListener("keydown", this.handleKeydown)
      this.#dialog.removeEventListener("click", this.handleClick)
    }
  }

  handleKeydown(event) {
    if (event.key === "Escape" && this.isOpen) {
      this.close()
    }
  }

  handleClick(event) {
    // Close if clicking the backdrop (dialog element itself)
    // if (event.target === this.#dialog && this.#closeOnOutsideClick) {
    //   this.close()
    // }
  }

  open() {
    if (!this.#dialog) return

    // Remove closing class if present (e.g., if reopening while closing)
    this.#dialog.classList.remove("closing")

    if (typeof this.#dialog.showPopover === "function") {
      this.#dialog.showPopover()
    } else if (typeof this.#dialog.showModal === "function") {
      this.#dialog.showModal()
    } else {
      this.#dialog.setAttribute("open", "")
    }

    document.documentElement.classList.add("pl-dialog-open")
    this.#focusFirstElement()
    this.dispatchEvent(new CustomEvent("pl-dialog:opened", { bubbles: true }))
  }

  close() {
    if (!this.#dialog) return

    // Mark that we're closing programmatically
    this.#isClosingProgrammatically = true

    // Add closing class to trigger CSS animation
    this.#dialog.classList.add("closing")

    let hasClosed = false

    const actuallyClose = () => {
      if (hasClosed) return
      hasClosed = true

      // Don't remove closing class here - let the toggle handler do it
      // This ensures the toggle event sees it as a programmatic close

      // Actually close the dialog
      if (typeof this.#dialog.hidePopover === "function") {
        this.#dialog.hidePopover()
      } else if (typeof this.#dialog.close === "function") {
        this.#dialog.close()
      } else {
        this.#dialog.removeAttribute("open")
      }

      document.documentElement.classList.remove("pl-dialog-open")
      this.dispatchEvent(new CustomEvent("pl-dialog:closed", { bubbles: true }))
    }

    // PRIMARY: Timeout-based approach (works in Safari)
    const timeoutId = setTimeout(() => {
      actuallyClose()
    }, 450) // Match animation duration

    // SECONDARY: Animationend optimization (close early if event fires)
    const handleAnimationEnd = (e) => {
      if (
        e.target === this.#dialog &&
        e.animationName &&
        e.animationName.startsWith("dialog-close")
      ) {
        clearTimeout(timeoutId)
        this.#dialog.removeEventListener("animationend", handleAnimationEnd)
        actuallyClose()
      }
    }

    this.#dialog.addEventListener("animationend", handleAnimationEnd, { once: true })
  }

  #focusFirstElement() {
    const firstFocusable = this.#dialog.querySelector('button, a, [tabindex]:not([tabindex="-1"])')
    firstFocusable?.focus()
  }

  get isOpen() {
    return this.#dialog?.matches(":popover-open") ?? false
  }

  /**
   * Re-queries the dialog element (useful after DOM morphing)
   */
  reinitDialog() {
    this.#dialog = this.querySelector("dialog") || (this instanceof HTMLDialogElement ? this : null)
  }
}

if (!customElements.get("pl-dialog")) {
  customElements.define("pl-dialog", PlDialog)
}
